/**
 * LayerSettings — Auto-generated settings controls for the selected layer.
 */

import { CustomLayerDefinition } from '../types';
import { getLayerSchema } from '../layerSchemas';
import ReactivityMapper from './ReactivityMapper';

interface LayerSettingsProps {
  layer: CustomLayerDefinition;
  onUpdateSetting: (key: string, value: number | string | boolean) => void;
  onUpdateOpacity: (opacity: number) => void;
  onUpdateBlendMode: (mode: 'normal' | 'additive' | 'multiply') => void;
  onUpdateLabel: (label: string) => void;
  onUpdateReactivity: (key: string, source: string, intensity: number, smoothing: number) => void;
}

export default function LayerSettings({
  layer, onUpdateSetting, onUpdateOpacity, onUpdateBlendMode, onUpdateLabel, onUpdateReactivity,
}: LayerSettingsProps) {
  const schema = getLayerSchema(layer.type);
  if (!schema) return null;

  return (
    <div className="space-y-3">
      {/* Layer name */}
      <div>
        <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Layer Name</label>
        <input
          type="text"
          value={layer.label}
          onChange={e => onUpdateLabel(e.target.value)}
          className="w-full bg-gray-800 rounded px-2 py-1 text-xs text-white border border-gray-700 focus:border-purple-500 outline-none"
        />
      </div>

      {/* Opacity + Blend Mode */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">
            Opacity: {Math.round(layer.opacity * 100)}%
          </label>
          <input
            type="range"
            min={0} max={1} step={0.05}
            value={layer.opacity}
            onChange={e => onUpdateOpacity(parseFloat(e.target.value))}
            className="w-full accent-purple-500"
          />
        </div>
        <div className="w-28">
          <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">Blend</label>
          <select
            value={layer.blendMode}
            onChange={e => onUpdateBlendMode(e.target.value as 'normal' | 'additive' | 'multiply')}
            className="w-full bg-gray-800 rounded px-2 py-1 text-xs text-white border border-gray-700 outline-none"
          >
            <option value="normal">Normal</option>
            <option value="additive">Additive</option>
            <option value="multiply">Multiply</option>
          </select>
        </div>
      </div>

      {/* Per-type settings */}
      {schema.settings.map(setting => {
        const value = layer.settings[setting.key] ?? setting.defaultValue;

        if (setting.type === 'range') {
          return (
            <div key={setting.key}>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">
                {setting.label}: {typeof value === 'number' ? (Number.isInteger(setting.step ?? 1) ? value : value.toFixed(2)) : value}
              </label>
              <input
                type="range"
                min={setting.min} max={setting.max} step={setting.step}
                value={typeof value === 'number' ? value : 0}
                onChange={e => onUpdateSetting(setting.key, parseFloat(e.target.value))}
                className="w-full accent-purple-500"
              />
            </div>
          );
        }

        if (setting.type === 'select' && setting.options) {
          return (
            <div key={setting.key}>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-1">{setting.label}</label>
              <select
                value={String(value)}
                onChange={e => onUpdateSetting(setting.key, e.target.value)}
                className="w-full bg-gray-800 rounded px-2 py-1 text-xs text-white border border-gray-700 outline-none"
              >
                {setting.options.map(opt => (
                  <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
                ))}
              </select>
            </div>
          );
        }

        if (setting.type === 'toggle') {
          return (
            <label key={setting.key} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={!!value}
                onChange={e => onUpdateSetting(setting.key, e.target.checked)}
                className="rounded accent-purple-500"
              />
              {setting.label}
            </label>
          );
        }

        return null;
      })}

      {/* Audio reactivity */}
      <ReactivityMapper
        layer={layer}
        schema={schema}
        onUpdate={onUpdateReactivity}
      />
    </div>
  );
}
